'use client';

import type { ReactNode } from 'react';
import { Settings2 } from 'lucide-react';
import { clsx } from 'clsx';
import { resolveLabelColor } from './tag-colors';

export interface TagFilterProps {
  /** Alle vom Kunden angelegten Labels (in sortOrder-Reihenfolge). */
  labels: string[];
  /** Projekt-Anzahl je Label (für die Zähler-Badges). */
  counts: Record<string, number>;
  /** Gesamtzahl (für "Alle {n}"). */
  total: number;
  /** Aktive Filter (Multi-Select-Union wie bisher). */
  activeLabels: string[];
  /** Name → persistierte Palette-Farbe (null = aus Name abgeleitet). */
  colorByLabel: Record<string, string | null>;
  onToggle: (label: string) => void;
  onClear: () => void;
  /** Öffnet das „Labels verwalten"-Sheet/Modal. */
  onOpenManage: () => void;
  /**
   * Optionaler Chip, der direkt nach „Alle" eingefügt wird (z. B. der
   * Favoriten-„Angepinnt"-Pseudofilter). Projekte nutzt ihn nicht.
   */
  afterAll?: ReactNode;
}

/**
 * Label-Filter-Pills nach Design (Image #1): farbiger Punkt + Name + Zähler.
 * Aktive Pills sind orange gefüllt. Die Pills **brechen** bei Platzmangel auf
 * mehrere Zeilen um (kein horizontales Scrollen mehr). Der gestrichelte
 * „Verwalten"-Pill öffnet das globale „Labels verwalten"-Sheet.
 */
export function TagFilter({
  labels,
  counts,
  total,
  activeLabels,
  colorByLabel,
  onToggle,
  onClear,
  onOpenManage,
  afterAll,
}: TagFilterProps) {
  // Erst zugewiesene Labels (in sortOrder), danach die noch unzugewiesenen.
  // So bleiben die „aktiven" Labels vorne; unzugewiesene erscheinen ausgegraut
  // als Anhang (siehe Render-Branch unten).
  const assigned = labels.filter((l) => (counts[l] ?? 0) > 0);
  const unassigned = labels.filter((l) => (counts[l] ?? 0) === 0);
  const visibleLabels = [...assigned, ...unassigned];

  return (
    <div className="flex flex-wrap gap-1.5 py-1">
      {/* Alle */}
      <button
        type="button"
        onClick={onClear}
        className={clsx(
          'inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[13px] font-semibold whitespace-nowrap transition-colors',
          activeLabels.length === 0
            ? 'bg-primary border-primary text-white'
            : 'bg-white border-gray-200 text-secondary hover:bg-gray-50',
        )}
      >
        {/* Text + Zähler in EINEM Inline-Kontext: so teilen sie sich per
            CSS-Default die Grundlinie (sonst sitzt der kleinere Zähler optisch
            zu hoch, weil Flex-Items einzeln zentriert werden). */}
        <span>
          Alle
          <span
            className={clsx(
              'ml-1.5 text-[11px] font-bold tabular-nums',
              activeLabels.length === 0 ? 'text-white/80' : 'text-gray-400',
            )}
          >
            {total}
          </span>
        </span>
      </button>

      {afterAll}

      {visibleLabels.map((label) => {
        // Unzugewiesene Labels (Count 0) werden nur angezeigt, sind aber
        // ausgegraut und nicht klickbar — ein Filter darauf wäre ohnehin leer.
        const isUnassigned = (counts[label] ?? 0) === 0;
        const active = !isUnassigned && activeLabels.includes(label);
        return (
          <button
            key={label}
            type="button"
            disabled={isUnassigned}
            onClick={isUnassigned ? undefined : () => onToggle(label)}
            className={clsx(
              'inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[13px] font-semibold whitespace-nowrap transition-colors',
              isUnassigned
                ? 'bg-white border-gray-200 text-gray-400 opacity-60 cursor-default'
                : active
                  ? 'bg-primary border-primary text-white'
                  : 'bg-white border-gray-200 text-secondary hover:bg-gray-50',
            )}
          >
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{
                backgroundColor: active
                  ? 'rgba(255,255,255,0.85)'
                  : resolveLabelColor(colorByLabel[label], label),
              }}
            />
            {/* Name + Zähler teilen sich die Grundlinie (ein Inline-Kontext). */}
            <span>
              {label}
              <span
                className={clsx(
                  'ml-1.5 text-[11px] font-bold tabular-nums',
                  active ? 'text-white/80' : 'text-gray-400',
                )}
              >
                {counts[label] ?? 0}
              </span>
            </span>
          </button>
        );
      })}

      {/* „Verwalten" ist immer verfügbar — auch wenn es noch kein Label gibt,
          damit das erste Label über das Sheet angelegt werden kann. */}
      <button
        type="button"
        onClick={onOpenManage}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-dashed border-gray-400 text-secondary bg-white text-[13px] font-semibold whitespace-nowrap transition-colors hover:bg-gray-50 hover:border-gray-500"
      >
        <Settings2 size={14} />
        Verwalten
      </button>
    </div>
  );
}
